import React, { useState, useEffect, useRef } from "react"
import { createRoot } from "react-dom/client"
import { HtmlEditorCommander } from "aldoo-ra/Commander"

const { runCommand } = HtmlEditorCommander
import Tooltip from "aldoo-ra/Tooltip"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import ClassPropertyDrawer from "../../components/ClassPropertyDrawer"
import { useStyleGeneration } from "../../StyleClassEditor/hooks/useStyleGeneration"
import { getDefaultProperties } from "../../StyleClassEditor/data/property-definitions"
import PubSub from "pubsub-js"

const HorizontalButtonOptionsMenu = ({ selectedButton, onClose, onButtonUpdated, variables = {}, onClassSelection }) => {
  const [selectedStyle, setSelectedStyle] = useState([])
  const [selectedAlignment, setSelectedAlignment] = useState([])
  const [elementClasses, setElementClasses] = useState([])

  const { extractClassProperties, parseOriginalClassesFromHTML } = useStyleGeneration()

  // Improved CSS extraction that handles malformed CSS better
  const improvedExtractClassProperties = (htmlContent, className) => {
    const properties = {}
    const escapedClassName = className.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

    // Find the class definition start
    const classStart = htmlContent.indexOf(`.${className}`)
    if (classStart === -1) return properties

    // Find the opening brace
    const openBrace = htmlContent.indexOf('{', classStart)
    if (openBrace === -1) return properties

    // Find the closing brace, but handle cases where it might be missing
    let closeBrace = htmlContent.indexOf('}', openBrace)
    if (closeBrace === -1) {
      // If no closing brace, take everything until the next class or end of content
      const nextClass = htmlContent.indexOf('.', openBrace + 1)
      closeBrace = nextClass !== -1 ? nextClass - 1 : htmlContent.length
    }

    const cssRules = htmlContent.substring(openBrace + 1, closeBrace)

    // Parse CSS rules, handling various formats
    const rules = cssRules.split(';')
    rules.forEach(rule => {
      const trimmedRule = rule.trim()
      if (!trimmedRule) return

      const colonIndex = trimmedRule.indexOf(':')
      if (colonIndex > 0) {
        const property = trimmedRule.substring(0, colonIndex).trim()
        let value = trimmedRule.substring(colonIndex + 1).trim()

        // Clean up value (remove trailing semicolons, incomplete brackets, etc.)
        value = value.replace(/;+$/, '').trim()

        if (property && value) {
          properties[property] = value
        }
      }
    })

    return properties
  }

  // Check if button contains an image
  const buttonElement = selectedButton?.classList.contains("editor-button")
    ? selectedButton
    : selectedButton?.querySelector(".editor-button")
  const containsImage = buttonElement?.querySelector("img") !== null
  const buttonImage = buttonElement?.querySelector("img")

  // Extract classes from the selected button element
  useEffect(() => {
    if (selectedButton) {
      // Get the original HTML content from content manager - NO FALLBACKS
      const getCurrentContent = () => {
        return new Promise((resolve, reject) => {
          const token = PubSub.subscribe("html-editor:current-content-response", (msg, data) => {
            PubSub.unsubscribe(token)
            if (data.htmlContent) {
              resolve(data.htmlContent)
            } else {
              reject(new Error("No HTML content received from content manager"))
            }
          })

          // Request current content from content manager
          PubSub.publish("html-editor:get-current-content", {
            requestId: Date.now()
          })

          // Timeout without fallback - we MUST get content from content manager
          setTimeout(() => {
            PubSub.unsubscribe(token)
            reject(new Error("Timeout waiting for HTML content from content manager"))
          }, 1000)
        })
      }

      getCurrentContent()
        .then((htmlContent) => {
          if (!htmlContent) {
            return
          }

          // Get only the original classes that were defined in the source CSS
          const originalClassesInHTML = parseOriginalClassesFromHTML(htmlContent)

          // Extract classes from the button element
          const className = selectedButton.className || ""
          const classNames = className.split(/\s+/).filter(cls => cls.trim() && !cls.startsWith('ae-') && !cls.startsWith('editor-'))

          if (classNames.length > 0) {
            // Filter to only include classes that exist in the original source AND are on this element
            const originalElementClasses = classNames.filter(cls => originalClassesInHTML.includes(cls))

            if (originalElementClasses.length > 0) {
              // Create class objects with properties from HTML, but only for original classes
              const classes = originalElementClasses.map(cls => {
                // Use improved extraction that handles malformed CSS better
                const extractedProps = improvedExtractClassProperties(htmlContent, cls)

                const classObj = {
                  id: cls,
                  name: cls,
                  value: cls,
                  properties: {
                    ...getDefaultProperties(),
                    ...extractedProps
                  },
                  extractedProperties: extractedProps, // Keep original extracted props for counting
                  hasCustomCSS: Object.keys(extractedProps).length > 0,
                  isOriginal: true // Mark as original source class
                }
                return classObj
              })

              setElementClasses(classes)
            } else {
              setElementClasses([])
            }
          } else {
            setElementClasses([])
          }
        })
        .catch((error) => {
          setElementClasses([])
        })
    }
  }, [selectedButton, extractClassProperties, parseOriginalClassesFromHTML])

  // Dropdown options
  const styleOptions = [
    { value: "primary", label: "Primary" },
    { value: "secondary", label: "Secondary" },
    { value: "outline", label: "Outline" },
    { value: "ghost", label: "Ghost" },
    { value: "gradient", label: "Gradient" }
  ]

  const alignmentOptions = [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" }
  ]

  // Initialize states based on current button
  useEffect(() => {
    if (!selectedButton) return
    // Detect current style and alignment from button classes/styles
    // This would need to be implemented based on your button styling system
  }, [selectedButton])

  // Action handlers
  const handleEditText = () => {
    onClose()
    runCommand("button.text.edit", { target: selectedButton, variables })
  }

  const handleEditLink = () => {
    const linkElement = selectedButton.closest('a') || selectedButton
    if (linkElement && linkElement.tagName === 'A') {
      onClose()
      runCommand("link.edit", { target: linkElement, variables })
    }
  }

  const handleStyleChange = (values) => {
    setSelectedStyle(values)
    if (!values || values.length === 0) {
      if (onButtonUpdated) onButtonUpdated(selectedButton)
      return
    }

    const style = values[0].value
    runCommand("button.style", { selectedButton, style })
    if (onButtonUpdated) onButtonUpdated(selectedButton)
  }

  const handleAlignmentChange = (values) => {
    setSelectedAlignment(values)
    if (!values || values.length === 0) {
      if (onButtonUpdated) onButtonUpdated(selectedButton)
      return
    }

    const alignment = values[0].value
    runCommand("button.align", { selectedButton, alignment })
    if (onButtonUpdated) onButtonUpdated(selectedButton)
  }

  const handleDuplicate = () => {
    runCommand("button.duplicate", {
      selectedButton,
      variables,
      onDuplicateComplete: (newButton) => {
        if (onButtonUpdated) onButtonUpdated(newButton || selectedButton)
      },
    })
    onClose()
  }

  const handleDelete = () => {
    runCommand("button.delete", {
      selectedButton,
      onDeleteComplete: () => {
        if (onButtonUpdated) onButtonUpdated(null)
      },
    })
    onClose()
  }

  const handleAddImage = () => {
    onClose()
    showImageSelector(selectedButton, onButtonUpdated)
  }

  const handleViewSource = () => {
    onClose()
    runCommand("button.source.edit", { target: selectedButton })
  }

  const handleAdvancedStyling = () => {
    onClose()
    runCommand("button.style.edit", { target: selectedButton })
  }

  // Handle class selection from dropdown
  const handleClassSelection = (selectedOption) => {
    if (onClassSelection) {
      onClassSelection(selectedOption)
    }
  }


  // Format classes for dropdown display
  const classDropdownOptions = elementClasses.map(cls => {
    // Count only the actually extracted properties, not the merged ones
    const extractedPropCount = Object.keys(cls.extractedProperties || {}).length

    return {
      value: cls.name,
      name: `.${cls.name}`,
      description: `${extractedPropCount} properties`,
      classData: cls
    }
  })

  // Image-specific handlers (when button contains an image)
  const handleEditImageInButton = () => {
    if (buttonImage) {
      onClose()
      // Delegate to the image plugin's horizontal menu, which will automatically
      // detect the <a> parent and show link editing options
      runCommand("image.optionsMenu", {
        selectedImage: buttonImage,
        onImageUpdated: onButtonUpdated,
        variables
      })
    }
  }

  const ToolbarButton = ({ onClick, tooltip, active = false, children }) => (
    <Tooltip content={tooltip} variant="dark" zIndex={10000}>
      <button
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (onClick) {
            onClick(e)
          }
        }}
        onMouseDown={(e) => {
          e.stopPropagation()
        }}
        className={`flex items-center justify-center w-10 h-10 rounded-lg border transition-colors ${
          active
            ? 'bg-admin_primary text-white border-admin_primary'
            : 'bg-admin_paper text-admin_text_secondary border-admin_bordered hover:bg-admin_neutral_1'
        }`}
      >
        {children}
      </button>
    </Tooltip>
  )

  return (
    <div className="relative">
      {/* Add CSS to hide dropdown arrows */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .ae-hide-dropdown-arrow svg[data-slot="icon"] {
            display: none !important;
          }
          .ae-hide-dropdown-arrow .w-5.h-5.transition-transform {
            display: none !important;
          }
        `
      }} />

      {/* Horizontal Toolbar */}
      <div className="bg-admin_paper border border-admin_bordered rounded-lg shadow-lg p-2 w-fit">
        <div className="flex flex-col gap-2">
          {/* First Row */}
          <div className="flex items-center gap-2">
            {/* Edit Text */}
            <ToolbarButton
              onClick={handleEditText}
              tooltip="Edit Text"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </ToolbarButton>

            {/* Edit Link - Only show if button is inside a link */}
            {selectedButton?.closest('a') && (
              <ToolbarButton
                onClick={handleEditLink}
                tooltip="Edit Link"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </ToolbarButton>
            )}

            {/* Style Dropdown */}
            <DropdownSelect
              variant="inline"
              options={styleOptions}
              value={selectedStyle}
              onChange={handleStyleChange}
              placeholder="Style"
              enableResetButton={false}
              showCheckbox={false}
              className="ae-hide-dropdown-arrow"
              onValueItemRender={(item) => {
                const tooltipText = item ? `Style: ${item.label}` : "Button Style"
                return (
                  <Tooltip content={tooltipText} variant="dark" zIndex={10000}>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V9a2 2 0 012-2h2.5l4-4h.5a2 2 0 011.6.8L14 7h3a2 2 0 012 2v8a4 4 0 01-4 4H7z" />
                      </svg>
                    </div>
                  </Tooltip>
                )
              }}
              onItemRender={(item) => (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V9a2 2 0 012-2h2.5l4-4h.5a2 2 0 011.6.8L14 7h3a2 2 0 012 2v8a4 4 0 01-4 4H7z" />
                  </svg>
                  <span>{item.label}</span>
                </div>
              )}
            />

            {/* Alignment Dropdown */}
            <DropdownSelect
              variant="inline"
              options={alignmentOptions}
              value={selectedAlignment}
              onChange={handleAlignmentChange}
              placeholder="Align"
              enableResetButton={false}
              showCheckbox={false}
              className="ae-hide-dropdown-arrow"
              onValueItemRender={(item) => {
                const iconPath = item ?
                  (item.value === "left" ? "M4 6h16M4 12h8M4 18h16" :
                   item.value === "center" ? "M4 6h16M8 12h8M4 18h16" :
                   "M4 6h16M12 12h8M4 18h16") :
                  "M4 6h16M4 12h16M4 18h16"

                const tooltipText = item ? `Align: ${item.label}` : "Button Alignment"

                return (
                  <Tooltip content={tooltipText} variant="dark" zIndex={10000}>
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                      </svg>
                    </div>
                  </Tooltip>
                )
              }}
              onItemRender={(item) => (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d={item.value === "left" ? "M4 6h16M4 12h8M4 18h16" :
                            item.value === "center" ? "M4 6h16M8 12h8M4 18h16" :
                            "M4 6h16M12 12h8M4 18h16"} />
                  </svg>
                  <span>{item.label}</span>
                </div>
              )}
            />
          </div>

          {/* Second Row */}
          <div className="flex items-center gap-2">
            {/* Duplicate */}
            <ToolbarButton
              onClick={handleDuplicate}
              tooltip="Duplicate"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </ToolbarButton>

            {/* Delete */}
            <ToolbarButton
              onClick={handleDelete}
              tooltip="Delete"
            >
              <svg className="w-5 h-5 text-admin_error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H9a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </ToolbarButton>

            {/* Class Selector - Only show if element has classes */}
            {elementClasses.length > 0 ? (
              <div className="relative">
                <DropdownSelect
                  variant="inline"
                  options={classDropdownOptions}
                  placeholder="Classes"
                  searchEnabled={true}
                  allowEmptySelection={false}
                  showCheckbox={false}
                  className="ae-hide-dropdown-arrow"
                  onValueItemRender={(item) => {
                    const tooltipText = item ? `Class: ${item.name}` : `${elementClasses.length} CSS classes`
                    return (
                      <Tooltip content={tooltipText} variant="dark" zIndex={10000}>
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg border border-admin_bordered bg-admin_paper hover:bg-admin_neutral_1 transition-colors relative">
                          <svg 
                            xmlns="http://www.w3.org/2000/svg" 
                            fill="none" 
                            viewBox="0 0 24 24" 
                            strokeWidth={1.5} 
                            stroke="currentColor" 
                            className="w-5 h-5 text-admin_neutral_3"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" 
                            />
                          </svg>

                          <span className="absolute -top-1 -right-1 bg-admin_info text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {elementClasses.length}
                          </span>
                        </div>
                      </Tooltip>
                    )
                  }}
                  onItemRender={(item) => (
                    <div className="flex flex-col">
                      <span className="font-mono text-sm text-admin_primary">{item.name}</span>
                      {item.description && (
                        <span className="text-xs text-admin_text_secondary">{item.description}</span>
                      )}
                    </div>
                  )}
                  onChange={handleClassSelection}
                />
              </div>
            ) : (
              // Fallback to Advanced Styling if no classes
              <ToolbarButton
                onClick={handleAdvancedStyling}
                tooltip="Advanced Styling"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V9a2 2 0 012-2h2.5l4-4h.5a2 2 0 011.6.8L14 7h3a2 2 0 012 2v8a4 4 0 01-4 4H7z M16 11l2 2-5.5 5.5H10v-2.5L15.5 11H16z" />
                </svg>
              </ToolbarButton>
            )}

            {/* Add Image or Edit Image */}
            {containsImage ? (
              <ToolbarButton
                onClick={handleEditImageInButton}
                tooltip="Edit Image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </ToolbarButton>
            ) : (
              <ToolbarButton
                onClick={handleAddImage}
                tooltip="Add Image"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </ToolbarButton>
            )}

            {/* View Source */}
            <ToolbarButton
              onClick={handleViewSource}
              tooltip="View Source"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
            </ToolbarButton>
          </div>
        </div>
      </div>
    </div>
  )
}

// Store reference to current click outside handler
let currentClickOutsideHandler = null

const removeExistingMenu = () => {
  try {
    // Remove the previous click outside handler first
    if (currentClickOutsideHandler) {
      document.removeEventListener("mousedown", currentClickOutsideHandler)
      currentClickOutsideHandler = null
    }

    const existingMenu = document.querySelector(".ae-horizontal-button-options-menu-container")
    if (existingMenu) {
      // Force cleanup of any tooltips before removing the menu
      const tooltipTriggers = existingMenu.querySelectorAll('button, [role="button"]')
      tooltipTriggers.forEach(trigger => {
        trigger.dispatchEvent(new MouseEvent('mouseleave', {
          view: window,
          bubbles: true,
          cancelable: true
        }))
      })

      existingMenu.remove()
    }
  } catch (error) {
    console.error('Error in removeExistingMenu:', error)
  }
}

// Wrapper component that handles positioning and drawer state
const PositionedHorizontalMenu = ({ selectedButton, onButtonUpdated, onClose, variables }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [isPropertyDrawerOpen, setIsPropertyDrawerOpen] = useState(false)
  const [selectedClassForDrawer, setSelectedClassForDrawer] = useState(null)
  const [htmlContent, setHtmlContent] = useState("")
  const menuRef = useRef(null)

  useEffect(() => {
    if (!selectedButton) return

    const updatePosition = () => {
      // Get the actual button element for positioning (not the wrapper or link)
      const actualButton = selectedButton.classList.contains("editor-button")
        ? selectedButton
        : selectedButton.querySelector(".editor-button") || selectedButton

      const buttonRect = actualButton.getBoundingClientRect()
      const scrollY = window.pageYOffset || document.documentElement.scrollTop
      const scrollX = window.pageXOffset || document.documentElement.scrollLeft

      // Calculate the center of the button
      const buttonCenterX = buttonRect.left + scrollX + (buttonRect.width / 2)

      // Get menu width to center it properly
      let menuWidth = 0
      if (menuRef.current) {
        menuWidth = menuRef.current.getBoundingClientRect().width
      }

      // Get menu height to position it properly above the button
      let menuHeight = 0
      if (menuRef.current) {
        menuHeight = menuRef.current.getBoundingClientRect().height
      }

      setPosition({
        top: buttonRect.top + scrollY - menuHeight - 10, // Position above button with 10px gap
        left: buttonCenterX - (menuWidth / 2)
      })
    }

    // Initial position
    updatePosition()

    // Update position again after menu renders to get accurate width
    setTimeout(updatePosition, 0)

    // Update position on scroll, resize, or DOM changes
    window.addEventListener('scroll', updatePosition)
    window.addEventListener('resize', updatePosition)

    const observer = new MutationObserver(updatePosition)
    if (selectedButton.parentElement) {
      observer.observe(selectedButton.parentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      })
    }

    // Watch for menu size changes
    let resizeObserver = null
    if (menuRef.current) {
      resizeObserver = new ResizeObserver(updatePosition)
      resizeObserver.observe(menuRef.current)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [selectedButton])

  // Handle class selection callback
  const handleClassSelection = (selectedOption) => {
    // Handle both single object and array formats
    const option = Array.isArray(selectedOption) ? selectedOption[0] : selectedOption

    if (option && option.classData) {
      // FIRST: Close the horizontal menu immediately
      onClose() // Close the horizontal menu

      // THEN: Set up property drawer (with slight delay to ensure menu closes)
      setTimeout(() => {
        setSelectedClassForDrawer(option.classData)
        setIsPropertyDrawerOpen(true)

        // Get HTML content (full document to include style tags)
        const editorElement = selectedButton.closest('[contenteditable="true"]')
        if (editorElement && editorElement.ownerDocument) {
          setHtmlContent(editorElement.ownerDocument.documentElement.outerHTML)
        } else if (editorElement) {
          setHtmlContent(editorElement.innerHTML || "")
        }
      }, 10) // Small delay to ensure menu closes first
    }
  }

  return (
    <>
      <div
        ref={menuRef}
        className="ae-horizontal-button-options-menu fixed z-[1300]"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`
        }}
      >
        <HorizontalButtonOptionsMenu
          selectedButton={selectedButton}
          onButtonUpdated={onButtonUpdated}
          onClose={onClose}
          variables={variables}
          onClassSelection={handleClassSelection}
        />
      </div>

      {/* Class Property Drawer */}
      <ClassPropertyDrawer
        isOpen={isPropertyDrawerOpen}
        onClose={() => {
          setIsPropertyDrawerOpen(false)
          setSelectedClassForDrawer(null)
        }}
        selectedClass={selectedClassForDrawer}
        htmlContent={htmlContent}
      />
    </>
  )
}

const HorizontalButtonOptionsMenuMain = (data) => {
  const { selectedButton, onButtonUpdated, variables } =
    typeof data === "object" ? data : { selectedButton: data }

  if (!selectedButton) return

  // Don't show horizontal menu if property editor is active
  if (window._isPropertyEditorActive) {

    return
  }
  removeExistingMenu()

  // Create container
  const menuContainer = document.createElement("div")
  menuContainer.className = "ae-horizontal-button-options-menu-container"
  document.body.appendChild(menuContainer)

  // Store the current selected button reference that can be updated
  let currentSelectedButton = selectedButton

  const handleClickOutside = (e) => {
    if (menuContainer && menuContainer.contains(e.target)) return
    if (e.target === selectedButton) return

    const dropdownPanels = document.querySelectorAll('[role="menu"], .ae-dropdown-panel')
    for (let panel of dropdownPanels) {
      if (panel.contains(e.target)) return
    }

    const tooltips = document.querySelectorAll('[class*="tooltip"], [data-tooltip]')
    for (let tooltip of tooltips) {
      if (tooltip.contains(e.target)) return
    }

    removeExistingMenu()
  }

  currentClickOutsideHandler = handleClickOutside
  document.addEventListener("mousedown", handleClickOutside)

  const root = createRoot(menuContainer)
  root.render(
    <PositionedHorizontalMenu
      selectedButton={currentSelectedButton}
      onButtonUpdated={onButtonUpdated}
      onClose={() => {
        removeExistingMenu()
      }}
      variables={variables}
    />
  )
}

// Function to show image selector for adding image to button
const showImageSelector = async (selectedButton, onButtonUpdated) => {
  if (!selectedButton) return
  removeExistingMenu()

  try {
    // Use the image plugin's add functionality and wait for the result
    const newImage = await runCommand("image.add", {
      selectionRange: null, // We don't need selection range for button context
      setSelectionRange: () => {}, // Provide a dummy function to avoid errors
    })

    if (newImage && selectedButton) {
      // Remove the image from its current location (it was auto-inserted by the image plugin)
      newImage.remove()

      // Get the button element (in case selectedButton is wrapper)
      const button = selectedButton.classList.contains("editor-button")
        ? selectedButton
        : selectedButton.querySelector(".editor-button")

      if (button) {
        // Get current button dimensions before clearing content
        const buttonRect = button.getBoundingClientRect()
        const currentWidth = buttonRect.width
        const currentHeight = buttonRect.height

        // Clear existing content and add the image
        button.innerHTML = ""
        button.appendChild(newImage)

        // Make sure button stays non-editable
        button.contentEditable = "false"

        // Add social-link class if it's not already present (for button with images)
        if (!button.classList.contains('social-link')) {
          button.classList.add('social-link')
        }

        // Remove button styling that interferes with image sizing
        button.style.padding = "0"
        button.style.width = "auto"
        button.style.height = "auto"
        button.style.minWidth = "auto"
        button.style.minHeight = "auto"

        // Set image to inherit the button's original dimensions
        newImage.style.width = `${currentWidth}px`
        newImage.style.height = `${currentHeight}px`
        newImage.style.cursor = "grab"
        newImage.style.display = "block"
        newImage.style.objectFit = "contain" // Preserve aspect ratio
        newImage.draggable = true

        // Don't set fixed width/height attributes - let CSS handle it
        newImage.removeAttribute('width')
        newImage.removeAttribute('height')

        // After image is added, automatically switch to image editor
        setTimeout(() => {
          // Close the current button menu
          removeExistingMenu()
          // Open the image horizontal menu instead
          runCommand("image.optionsMenu", {
            selectedImage: newImage,
            onImageUpdated: onButtonUpdated,
            variables: {}
          })
        }, 100)

        // Update button updated callback if available
        if (onButtonUpdated) onButtonUpdated(selectedButton)
      }
    }
  } catch (error) {
    console.error("Failed to add image to button:", error)
  }
}



// Helper function to check if an image is inside a button
export const isImageInButton = (image) => {
  return image && image.closest && image.closest('.editor-button') !== null
}

// Helper function to get the button containing an image
export const getButtonContainingImage = (image) => {
  return image && image.closest && image.closest('.editor-button')
}

export default HorizontalButtonOptionsMenuMain